import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Autocomplete,
  IconButton,
  LinearProgress,
  Divider,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SuperAdminService, {
  Company,
  TaskMigrationCounts,
  TaskMigrationData,
  MigrateAllTasksResult,
} from '../../services/SuperAdminService';
import SuperAdminLayout from './SuperAdminLayout';
import GuideButton from '../common/guide/GuideButton';

interface SuperAdminTaskMigrationProps {
  /** Modal içinde gösterildiğinde verilir; geri tuşu yerine kapatma butonu çalışır. */
  onClose?: () => void;
}

// .NET Data tipi-istatistiğini casing-toleranslı oku.
function stat(d: TaskMigrationData | undefined, key: string): { migrated: number; updated: number; errors: number } {
  const cap = key.charAt(0).toUpperCase() + key.slice(1);
  const s: any = (d && ((d as any)[cap] || (d as any)[key])) || {};
  return {
    migrated: Number(s.Migrated ?? s.migrated ?? 0) || 0,
    updated: Number(s.Updated ?? s.updated ?? 0) || 0,
    errors: Number(s.Errors ?? s.errors ?? 0) || 0,
  };
}

const SuperAdminTaskMigration: React.FC<SuperAdminTaskMigrationProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const embedded = typeof onClose === 'function';

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState<TaskMigrationCounts | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [result, setResult] = useState<TaskMigrationData | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Toplu taşıma durumu
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState<MigrateAllTasksResult | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number; name: string } | null>(null);
  const [bulkLog, setBulkLog] = useState<string[]>([]);

  // Toplu TARAMA (önce görevleri bul, yazma YOK) durumu
  const [bulkScanning, setBulkScanning] = useState(false);
  const [bulkPreview, setBulkPreview] = useState<{
    rows: Array<{ companyId: string; name: string; counts?: TaskMigrationCounts; error?: string }>;
    totals: {
      companies: number; withData: number; tasks: number; completions: number; teamProjects: number; yearlyPlans: number;
      single: number; daily: number; weekly: number; monthly: number; completed: number; missed: number;
    };
  } | null>(null);

  const isSuperAdmin = userDetails?.role === 'superadmin';

  useEffect(() => {
    const loadCompanies = async () => {
      setLoadingCompanies(true);
      setError(null);
      try {
        const list = await SuperAdminService.getInstance().getAllCompanies();
        setCompanies(list);
      } catch (err: any) {
        setError('Şirketler yüklenemedi: ' + (err?.message || 'bilinmeyen hata'));
      } finally {
        setLoadingCompanies(false);
      }
    };
    loadCompanies();
  }, []);

  const handleSelectCompany = (company: Company | null) => {
    setSelectedCompany(company);
    setPreview(null);
    setResult(null);
    setError(null);
    setSuccess(null);
  };

  const handleScan = async () => {
    if (!selectedCompany) return;
    setScanning(true);
    setError(null);
    setSuccess(null);
    setResult(null);
    try {
      const { counts } = await SuperAdminService.getInstance().getCompanyTasksPreview(selectedCompany.id);
      setPreview(counts);
      setSuccess(
        `Tarama tamamlandı: ${counts.tasks} görev, ${counts.completions} tamamlama, ${counts.teamProjects} ekip projesi, ${counts.yearlyPlans} yıllık plan.`
      );
    } catch (err: any) {
      setError('Tarama hatası: ' + (err?.response?.data?.error || err?.message || 'bilinmeyen hata'));
    } finally {
      setScanning(false);
    }
  };

  const describeError = (err: any): { text: string; status?: number } => {
    const data = err?.response?.data;
    const status: number | undefined = data?.status ?? err?.response?.status;
    const detail = data?.dotnetDetail || data?.error || data?.message || err?.message || 'bilinmeyen hata';
    return { text: `${detail}${status ? ` (HTTP ${status})` : ''}`, status };
  };

  const handleMigrate = async () => {
    if (!selectedCompany) return;
    setConfirmOpen(false);
    setMigrating(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await SuperAdminService.getInstance().migrateTasks(selectedCompany.id);
      setResult(res.result);
      const t = stat(res.result, 'tasks');
      const c = stat(res.result, 'completions');
      const orphan = (res.result?.OrphanPersonnelUids || (res.result as any)?.orphanPersonnelUids || []).length;
      setSuccess(
        `Taşıma tamamlandı: görev(+${t.migrated}/~${t.updated}), tamamlama(+${c.migrated}/~${c.updated})${orphan ? `, orphan uid: ${orphan}` : ''}.`
      );
    } catch (err: any) {
      const d = describeError(err);
      setError('Taşıma hatası: ' + d.text);
    } finally {
      setMigrating(false);
    }
  };

  // AŞAMA 1 — TARAMA (yazma YOK): tüm şirketlerin görevlerini bulur, her adımı console'a loglar.
  const handleScanAll = async () => {
    setError(null);
    setSuccess(null);
    setBulkResult(null);
    setBulkPreview(null);
    setBulkLog([]);

    const targets = [...companies];
    if (targets.length === 0) {
      setError('Taranacak şirket bulunamadı (şirket listesi boş).');
      return;
    }

    setBulkScanning(true);
    setBulkProgress({ current: 0, total: targets.length, name: '' });

    console.group('%c[GörevTaşıma] TARAMA → TÜM ŞİRKETLER (dry-run, yazma YOK)', 'color:#0288d1;font-weight:bold');
    const startLine = `🔍 Tarama başlıyor: ${targets.length} şirket taranacak (RTDB okunur, SQL'e yazılmaz).`;
    console.log(startLine);
    setBulkLog([startLine]);
    const t0 = performance.now();

    const svc = SuperAdminService.getInstance();
    const totals = {
      companies: targets.length, withData: 0, tasks: 0, completions: 0, teamProjects: 0, yearlyPlans: 0,
      single: 0, daily: 0, weekly: 0, monthly: 0, completed: 0, missed: 0,
    };
    const rows: NonNullable<typeof bulkPreview>['rows'] = [];

    for (let i = 0; i < targets.length; i++) {
      const c = targets[i];
      const idx = i + 1;
      setBulkProgress({ current: idx, total: targets.length, name: c.name });
      const ct0 = performance.now();
      try {
        const { counts } = await svc.getCompanyTasksPreview(c.id);
        const ms = Math.round(performance.now() - ct0);
        const has = (counts.tasks + counts.teamProjects + counts.yearlyPlans + counts.taskGroups) > 0;
        if (has) totals.withData += 1;
        totals.tasks += counts.tasks;
        totals.single += counts.single;
        totals.daily += counts.daily;
        totals.weekly += counts.weekly;
        totals.monthly += counts.monthly;
        totals.completions += counts.completions;
        totals.completed += counts.completionsCompleted;
        totals.missed += counts.completionsMissed;
        totals.teamProjects += counts.teamProjects;
        totals.yearlyPlans += counts.yearlyPlans;
        rows.push({ companyId: c.id, name: c.name, counts });
        const line = `🔎 [${idx}/${targets.length}] ${c.name} (${ms}ms) — görev ${counts.tasks} (normal ${counts.single}, günlük ${counts.daily}, haftalık ${counts.weekly}, aylık ${counts.monthly}), yıllık ${counts.yearlyPlans} | tamamlama ${counts.completions} (✓${counts.completionsCompleted} / ✗missed ${counts.completionsMissed}), slot ${counts.slots}, yorum ${counts.comments}, ekipP ${counts.teamProjects}, ekipGörev ${counts.teamTasks}, grup ${counts.taskGroups}` + (counts.completionsSkipped ? `, atlanacak-tamamlama ${counts.completionsSkipped}` : '');
        console.log(line, counts);
        setBulkLog((prev) => [line, ...prev]);
      } catch (err: any) {
        const d = describeError(err);
        rows.push({ companyId: c.id, name: c.name, error: d.text });
        const line = `⚠️ [${idx}/${targets.length}] ${c.name} — tarama HATASI: ${d.text}`;
        console.warn(line, err?.response?.data ?? err);
        setBulkLog((prev) => [line, ...prev]);
      }
      setBulkPreview({ rows: [...rows], totals: { ...totals } });
    }

    const elapsed = Math.round(performance.now() - t0);
    const doneLine = `🏁 Tarama bitti (${elapsed}ms): ${totals.withData}/${totals.companies} şirkette veri var | toplam görev ${totals.tasks} (normal ${totals.single}, günlük ${totals.daily}, haftalık ${totals.weekly}, aylık ${totals.monthly}), yıllık ${totals.yearlyPlans} | tamamlama ${totals.completions} (✓${totals.completed} / ✗missed ${totals.missed}), ekip projesi ${totals.teamProjects}.`;
    console.log(doneLine);
    console.groupEnd();
    setBulkLog((prev) => [doneLine, ...prev]);
    setBulkProgress(null);
    setBulkScanning(false);
    setSuccess(`Tarama tamamlandı: ${totals.withData}/${totals.companies} şirkette görev verisi bulundu. Taşımak için "Tümünü Taşı"ya basın.`);
  };

  // AŞAMA 2 — TAŞIMA (yazma): yalnız tarama yapıldıktan sonra; her adım console'a loglanır.
  const handleMigrateAll = async () => {
    setBulkConfirmOpen(false);
    setError(null);
    setSuccess(null);
    setBulkResult(null);
    setBulkLog([]);

    const targets = [...companies];
    if (targets.length === 0) {
      setError('Taşınacak şirket bulunamadı (şirket listesi boş).');
      return;
    }

    setBulkRunning(true);
    setBulkProgress({ current: 0, total: targets.length, name: '' });
    console.group('%c[GörevTaşıma] TAŞIMA → TÜM ŞİRKETLER (SQL\'e yazma)', 'color:#9c27b0;font-weight:bold');
    const startLine = `▶️ Başlıyor: ${targets.length} şirketin görevleri sırayla taşınacak.`;
    console.log(startLine);
    setBulkLog([startLine]);
    const tAll0 = performance.now();

    const svc = SuperAdminService.getInstance();
    const totals = {
      companies: targets.length,
      succeeded: 0,
      failed: 0,
      tasksMigrated: 0,
      tasksUpdated: 0,
      completionsMigrated: 0,
      completionsUpdated: 0,
      orphan: 0,
    };
    const results: MigrateAllTasksResult['results'] = [];

    for (let i = 0; i < targets.length; i++) {
      const c = targets[i];
      const idx = i + 1;
      setBulkProgress({ current: idx, total: targets.length, name: c.name });
      const ct0 = performance.now();
      try {
        const r = await svc.migrateTasks(c.id);
        const ms = Math.round(performance.now() - ct0);
        const ts = stat(r.result, 'tasks');
        const cs = stat(r.result, 'completions');
        const orphan = (r.result?.OrphanPersonnelUids || (r.result as any)?.orphanPersonnelUids || []).length;
        totals.succeeded += 1;
        totals.tasksMigrated += ts.migrated;
        totals.tasksUpdated += ts.updated;
        totals.completionsMigrated += cs.migrated;
        totals.completionsUpdated += cs.updated;
        totals.orphan += orphan;
        results.push({ companyId: c.id, success: true, counts: r.counts, result: r.result });
        const line = `✅ [${idx}/${targets.length}] ${c.name} — bitti (${ms}ms) | görev +${ts.migrated}/~${ts.updated}, tamamlama +${cs.migrated}/~${cs.updated}${orphan ? `, orphan ${orphan}` : ''}`;
        console.log(line, r.result);
        setBulkLog((prev) => [line, ...prev]);
      } catch (err: any) {
        const d = describeError(err);
        totals.failed += 1;
        results.push({ companyId: c.id, success: false, error: d.text, status: d.status });
        const line = `❌ [${idx}/${targets.length}] ${c.name} — HATA: ${d.text}`;
        console.warn(line, err?.response?.data ?? err);
        setBulkLog((prev) => [line, ...prev]);
      }
      setBulkResult({ totals: { ...totals }, results: [...results] });
    }

    const elapsedAll = Math.round(performance.now() - tAll0);
    const doneLine = `🏁 Tamamlandı (${elapsedAll}ms): ${totals.succeeded}/${totals.companies} başarılı, ${totals.failed} hata | görev +${totals.tasksMigrated}/~${totals.tasksUpdated}, tamamlama +${totals.completionsMigrated}/~${totals.completionsUpdated}.`;
    console.log(doneLine);
    console.groupEnd();
    setBulkLog((prev) => [doneLine, ...prev]);
    setBulkProgress(null);
    setSuccess(
      `Toplu taşıma tamamlandı: ${totals.succeeded}/${totals.companies} şirket başarılı, ${totals.failed} hata. ` +
      `Görev +${totals.tasksMigrated}/~${totals.tasksUpdated}, tamamlama +${totals.completionsMigrated}/~${totals.completionsUpdated}.`
    );
    setBulkRunning(false);
  };

  const bulkFailed = bulkResult?.results.filter((r) => !r.success) ?? [];
  const previewEmpty = preview && preview.tasks === 0 && preview.teamProjects === 0 && preview.yearlyPlans === 0 && preview.taskGroups === 0;

  const content = (
    <Box sx={{ p: embedded ? 3 : 0, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => (embedded ? onClose!() : navigate('/superadmin'))} size="small">
          {embedded ? <CloseIcon /> : <ArrowBackIcon />}
        </IconButton>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon /> Görev Taşıma (RTDB → SQL)
        </Typography>
        <GuideButton guideKey="superadmin.taskMigration" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Firebase RTDB'deki görev ağacını (tekil/günlük/haftalık/aylık görevler, tamamlama/kaçırma kayıtları,
        yorumlar, görev grupları, ekip projeleri ve yıllık planlar) şirketin kendi SQL veritabanına (tenant)
        taşır. Personel referansları Firebase UID ile korunur; görev fotoğrafları Storage'da kalır. Şirket şirket
        veya toplu çalışır; idempotenttir (tekrar çalıştırılabilir).
      </Typography>

      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
        <strong>GEÇİCİ ARAÇ.</strong> Görev yazımları çoğunlukla MOBİL'den RTDB'ye gittiği için, cutover sırasında
        (mobil SQL'e geçtikten sonra) bu araç son kez tekrar çalıştırılmalıdır. Görev↔SQL köprüsü kurulmaz;
        snapshot sonrası geç gelen eski-app yazımları kabul edilen risktir.
      </Alert>

      {!isSuperAdmin && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Bu işlem yalnızca süperadmin tarafından yapılabilir.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* 0) TOPLU TAŞIMA */}
      <Paper sx={{ p: 2, mb: 3, borderLeft: '4px solid', borderColor: 'secondary.main' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DoneAllIcon color="secondary" /> Toplu Taşıma (Tüm Şirketler)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {loadingCompanies
            ? 'Şirket listesi yükleniyor…'
            : <>Toplam <strong>{companies.length} şirket.</strong> </>}
          <strong>Önce "Tüm Şirketleri Tara"</strong> ile görevler bulunur (yazma YOK), sonra <strong>"Tümünü Taşı"</strong>
          ile taşınır. Her adım canlı log + tarayıcı console'una yazılır; idempotenttir; hata veren şirket atlanır.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="info"
            startIcon={bulkScanning ? <CircularProgress size={18} color="inherit" /> : <FindInPageIcon />}
            onClick={handleScanAll}
            disabled={!isSuperAdmin || bulkScanning || bulkRunning || migrating || scanning || loadingCompanies || companies.length === 0}
          >
            {bulkScanning ? 'Taranıyor...' : '1) Tüm Şirketleri Tara (Görevleri Bul)'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={bulkRunning ? <CircularProgress size={18} color="inherit" /> : <DoneAllIcon />}
            onClick={() => setBulkConfirmOpen(true)}
            disabled={!isSuperAdmin || !bulkPreview || bulkScanning || bulkRunning || migrating || scanning || loadingCompanies}
          >
            {bulkRunning ? 'Tüm şirketler taşınıyor...' : '2) Tümünü Taşı'}
          </Button>
        </Box>
        {!bulkPreview && !bulkScanning && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            "Tümünü Taşı" butonu, tarama tamamlanınca aktifleşir.
          </Typography>
        )}

        {/* Tarama özeti (taşımadan önce) */}
        {bulkPreview && !bulkResult && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              {([
                ['Verisi Olan Şirket', `${bulkPreview.totals.withData}/${bulkPreview.totals.companies}`],
                ['Toplam Görev', bulkPreview.totals.tasks],
                ['Tamamlanan', bulkPreview.totals.completed],
                ['Kaçırılan (missed)', bulkPreview.totals.missed],
                ['Normal (tekil)', bulkPreview.totals.single],
                ['Günlük', bulkPreview.totals.daily],
                ['Haftalık', bulkPreview.totals.weekly],
                ['Aylık', bulkPreview.totals.monthly],
                ['Yıllık Plan', bulkPreview.totals.yearlyPlans],
                ['Ekip Projesi', bulkPreview.totals.teamProjects],
              ] as [string, React.ReactNode][]).map(([label, val]) => (
                <Grid item xs={6} sm={3} md={2.4} key={label}>
                  <Card variant="outlined"><CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>{label}</Typography>
                    <Typography variant="h5">{val}</Typography>
                  </CardContent></Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {bulkProgress && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                İşleniyor: {bulkProgress.current}/{bulkProgress.total}
                {bulkProgress.name ? ` — ${bulkProgress.name}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                %{Math.round((bulkProgress.current / Math.max(bulkProgress.total, 1)) * 100)}
              </Typography>
            </Box>
            <LinearProgress
              color="secondary"
              variant="determinate"
              value={(bulkProgress.current / Math.max(bulkProgress.total, 1)) * 100}
            />
          </Box>
        )}

        {bulkLog.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: '#0d1117',
              color: '#c9d1d9',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              maxHeight: 260,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {bulkLog.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </Box>
        )}

        {bulkResult && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined"><CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Şirket (Başarılı/Toplam)</Typography>
                  <Typography variant="h5">{bulkResult.totals.succeeded}/{bulkResult.totals.companies}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined"><CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Hatalı Şirket</Typography>
                  <Typography variant="h5" color={bulkResult.totals.failed === 0 ? 'text.primary' : 'error'}>
                    {bulkResult.totals.failed}
                  </Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined"><CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Görev (Eklendi/Güncellendi)</Typography>
                  <Typography variant="h5">+{bulkResult.totals.tasksMigrated}/~{bulkResult.totals.tasksUpdated}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined"><CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Tamamlama (Eklendi/Güncellendi)</Typography>
                  <Typography variant="h5">+{bulkResult.totals.completionsMigrated}/~{bulkResult.totals.completionsUpdated}</Typography>
                </CardContent></Card>
              </Grid>
            </Grid>

            {bulkResult.totals.orphan > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Toplam <strong>{bulkResult.totals.orphan} orphan personel uid</strong> bulundu (merkezi SystemUsers'ta
                yok). Görev yine de yazıldı; kullanıcı taşıması tamamlanınca tekrar çalıştırın.
              </Alert>
            )}

            {bulkFailed.length > 0 && (
              <>
                <Alert severity="error" sx={{ mb: 1 }}>
                  {bulkFailed.length} şirket taşınamadı (aşağıda). Düzeltip toplu taşımayı tekrar çalıştırabilirsiniz.
                </Alert>
                <TableContainer sx={{ maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Şirket ID</TableCell>
                        <TableCell>Hata</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkFailed.map((r) => (
                        <TableRow key={r.companyId}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.companyId}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{r.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {bulkResult.totals.failed === 0 && bulkProgress === null && (
              <Alert severity="success">Tüm şirketlerin görevleri başarıyla taşındı.</Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* 1) Şirket seç + Tara (dry-run) */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          1. Şirket Seç (Tek Şirket — Önizlemeli / Dry-run)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            sx={{ minWidth: 360, flex: 1 }}
            options={companies}
            loading={loadingCompanies}
            value={selectedCompany}
            onChange={(_e, value) => handleSelectCompany(value)}
            getOptionLabel={(o) => `${o.name} (${o.id})`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Şirket ara / seç"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loadingCompanies ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant="outlined"
            startIcon={scanning ? <CircularProgress size={18} /> : <FindInPageIcon />}
            onClick={handleScan}
            disabled={!selectedCompany || scanning || migrating}
          >
            {scanning ? 'Taranıyor...' : 'Tara (Önizle)'}
          </Button>
        </Box>
      </Paper>

      {/* 2) Önizleme */}
      {preview && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            2. Önizleme — {selectedCompany?.name}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Görev tipleri</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {([
              ['Normal (tekil)', preview.single],
              ['Günlük', preview.daily],
              ['Haftalık', preview.weekly],
              ['Aylık', preview.monthly],
              ['Yıllık Plan', preview.yearlyPlans],
              ['Ekip Projesi', preview.teamProjects],
              ['Ekip Görevi', preview.teamTasks],
              ['Görev (toplam)', preview.tasks],
            ] as [string, number][]).map(([label, val]) => (
              <Grid item xs={6} sm={4} md={3} key={label}>
                <Card variant="outlined"><CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">{label}</Typography>
                  <Typography variant="h4">{val}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Tamamlama / kaçırma ve diğer</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {([
              ['Tamamlanan', preview.completionsCompleted],
              ['Kaçırılan (missed)', preview.completionsMissed],
              ['Tamamlama (toplam)', preview.completions],
              ['Slot', preview.slots],
              ['Yorum', preview.comments],
              ['Grup', preview.taskGroups],
              ['Ekip', preview.teamGroups],
              ['Ekip Üyesi', preview.teamMembers],
            ] as [string, number][]).map(([label, val]) => (
              <Grid item xs={6} sm={4} md={3} key={label}>
                <Card variant="outlined"><CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">{label}</Typography>
                  <Typography variant="h4">{val}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>

          {preview.completionsSkipped > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {preview.completionsSkipped} tamamlama kaydı, eşleşen görevi bulunamadığı için atlanacak (silinmiş görev).
            </Alert>
          )}

          {previewEmpty && (
            <Alert severity="info" sx={{ mb: 2 }}>Bu şirkette taşınacak görev verisi bulunamadı.</Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={migrating ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={!isSuperAdmin || migrating || !!previewEmpty}
            >
              {migrating ? 'Taşınıyor...' : "SQL'e Taşı"}
            </Button>
          </Box>
          {migrating && <LinearProgress sx={{ mt: 2 }} />}
        </Paper>
      )}

      {/* 3) Sonuç */}
      {result && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" /> 3. Sonuç
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tür</TableCell>
                  <TableCell align="right">Eklendi</TableCell>
                  <TableCell align="right">Güncellendi</TableCell>
                  <TableCell align="right">Hata</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ['Gruplar', 'groups'],
                  ['Görevler', 'tasks'],
                  ['Slotlar', 'slots'],
                  ['Tamamlamalar', 'completions'],
                  ['Yorumlar', 'comments'],
                  ['Ekipler', 'teamGroups'],
                  ['Ekip Üyeleri', 'teamMembers'],
                  ['Ekip Projeleri', 'teamProjects'],
                  ['Ekip Görevleri', 'teamTasks'],
                  ['Ekip Geçmişi', 'teamTaskHistory'],
                  ['Yıllık Planlar', 'yearlyPlans'],
                ].map(([label, key]) => {
                  const s = stat(result, key);
                  return (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">{s.migrated}</TableCell>
                      <TableCell align="right">{s.updated}</TableCell>
                      <TableCell align="right">{s.errors}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {(() => {
            const orphan = (result?.OrphanPersonnelUids || (result as any)?.orphanPersonnelUids || []) as string[];
            return orphan.length > 0 ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>{orphan.length} orphan personel uid</strong> — merkezi SystemUsers'ta bu şirkete bağlı
                bulunamadı (görev yine de yazıldı).
                <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem', maxHeight: 160, overflow: 'auto' }}>
                  {orphan.join(', ')}
                </Box>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mt: 2 }}>Görevler başarıyla taşındı (orphan=0).</Alert>
            );
          })()}
        </Paper>
      )}

      {/* Onay diyaloğu */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Taşıma Onayı</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <strong>{selectedCompany?.name}</strong> şirketinin{' '}
            <strong>{preview?.tasks ?? 0}</strong> görevi, <strong>{preview?.completions ?? 0}</strong> tamamlama
            kaydı, <strong>{preview?.teamProjects ?? 0}</strong> ekip projesi ve{' '}
            <strong>{preview?.yearlyPlans ?? 0}</strong> yıllık planı SQL'e taşınacak.
          </Typography>
          <Alert severity="info">İşlem idempotenttir; mevcut kayıtlar güncellenir, yenileri eklenir.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleMigrate} variant="contained" color="primary" disabled={migrating}>
            {migrating ? <CircularProgress size={22} /> : 'Taşı'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toplu taşıma onay diyaloğu */}
      <Dialog open={bulkConfirmOpen} onClose={() => setBulkConfirmOpen(false)}>
        <DialogTitle>Toplu Taşıma Onayı</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Tarama: <strong>{bulkPreview?.totals.withData ?? 0}</strong> şirkette veri bulundu (toplam{' '}
            <strong>{bulkPreview?.totals.tasks ?? 0}</strong> görev, <strong>{bulkPreview?.totals.completions ?? 0}</strong>{' '}
            tamamlama). <strong>{companies.length} şirket</strong> sırayla SQL'e taşınacak. Her şirket bitince ilerleme ve
            sonuç canlı gösterilir.
          </Typography>
          <Alert severity="info">Uzun sürebilir, lütfen sayfayı kapatmayın.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleMigrateAll} variant="contained" color="secondary" disabled={bulkRunning}>
            {bulkRunning ? <CircularProgress size={22} /> : 'Tümünü Taşı'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <SuperAdminLayout title="Görev Taşıma" icon={<AssignmentIcon />}>
      {content}
    </SuperAdminLayout>
  );
};

export default SuperAdminTaskMigration;
